import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle, ArrowsClockwise, WarningCircle, XCircle, Package } from "@phosphor-icons/react";

/**
 * Displays the outcome of a CSV catalog import in a rich dialog.
 * Expects: { inserted, updated, skipped, errors: [] }
 */
export default function CsvImportResultDialog({ open, onOpenChange, result, filename }) {
  if (!result) return null;
  const { inserted = 0, updated = 0, skipped = 0, errors = [] } = result;
  const total = inserted + updated + skipped;
  const success = inserted + updated;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[#111] border-white/10 text-white rounded-2xl" data-testid="csv-result-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Package size={20} weight="duotone" className="text-[#005CFF]" />
            Import CSV terminé
          </DialogTitle>
          {filename && (
            <DialogDescription className="text-[11px] text-white/50 font-mono truncate">
              {filename} · {total} ligne{total > 1 ? "s" : ""} traitée{total > 1 ? "s" : ""}
            </DialogDescription>
          )}
        </DialogHeader>

        {/* Result stats */}
        <div className="grid grid-cols-3 gap-2 mt-2">
          <StatCard
            icon={CheckCircle}
            label="Ajoutés"
            value={inserted}
            color="#10B981"
            testid="stat-inserted"
          />
          <StatCard
            icon={ArrowsClockwise}
            label="Mis à jour"
            value={updated}
            color="#005CFF"
            testid="stat-updated"
          />
          <StatCard
            icon={XCircle}
            label="Ignorés"
            value={skipped}
            color="#9CA3AF"
            testid="stat-skipped"
          />
        </div>

        {success > 0 && (
          <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/25 p-3 flex items-start gap-2 text-emerald-300 text-xs">
            <CheckCircle size={16} weight="fill" className="shrink-0 mt-0.5" />
            <div>
              <b>{success} article{success > 1 ? "s" : ""}</b> ajouté{success > 1 ? "s" : ""} au catalogue et disponible{success > 1 ? "s" : ""} pour les techniciens.
            </div>
          </div>
        )}

        {errors && errors.length > 0 && (
          <div className="rounded-xl bg-amber-500/10 border border-amber-500/25 p-3 space-y-1.5" data-testid="csv-errors">
            <div className="flex items-center gap-1.5 text-amber-300 text-xs font-semibold">
              <WarningCircle size={14} weight="fill" />
              {errors.length} avertissement{errors.length > 1 ? "s" : ""}
            </div>
            <div className="max-h-40 overflow-y-auto space-y-0.5 pr-1">
              {errors.map((e, i) => (
                <div key={`${i}-${(e || "").slice(0, 24)}`} className="text-[11px] text-amber-200/80 font-mono" data-testid={`csv-error-${i}`}>
                  · {e}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2 mt-2">
          <Button
            onClick={() => onOpenChange(false)}
            className="flex-1 bg-[#005CFF] hover:bg-[#0047CC]"
            data-testid="csv-result-close"
          >
            OK
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ icon: Icon, label, value, color, testid }) {
  return (
    <div
      className="rounded-xl border p-3 text-center"
      style={{ background: color + "10", borderColor: color + "40" }}
      data-testid={testid}
    >
      <Icon size={18} weight="duotone" style={{ color }} className="mx-auto" />
      <div className="font-display text-2xl font-bold mt-1 tabular-nums" style={{ color }}>{value}</div>
      <div className="text-[9px] uppercase tracking-widest text-white/60 mt-0.5">{label}</div>
    </div>
  );
}
